/**
 * Delivery System API Service
 * Connects to the Phase 1 & 2 backend APIs
 * 
 * Backend Endpoints:
 * - /api/delivery-management/* (10 endpoints - Admin/Manager)
 * - /api/delivery-agent/* (7 endpoints - Delivery Boy)
 * - /api/bottles/* (7 endpoints - Bottle Tracking)
 */

import axios from 'axios';

// Use the same base URL pattern as api.js
const API_BASE_URL = import.meta.env.DEV 
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle auth errors - clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * ========================================
 * DELIVERY MANAGEMENT API (Admin/Manager)
 * ========================================
 */
export const deliveryManagementAPI = {
  /**
   * Auto-create order groups based on GPS clustering
   * POST /api/delivery-management/auto-create-groups
   */
  autoCreateGroups: (deliveryDate, radiusKm = 1) => 
    api.post('/delivery-management/auto-create-groups', { deliveryDate, radiusKm }),

  /**
   * Get all order groups for a delivery date
   * GET /api/delivery-management/groups?date=YYYY-MM-DD
   */
  getGroups: (deliveryDate) => 
    api.get('/delivery-management/groups', { params: { date: deliveryDate } }),

  /**
   * Get single group details with members
   * GET /api/delivery-management/groups/:groupId
   */
  getGroupDetails: (groupId) => 
    api.get(`/delivery-management/groups/${groupId}`),

  /**
   * Manually create a new order group
   * POST /api/delivery-management/create-group
   */
  createGroup: (deliveryDate, groupName, orderUuids) => 
    api.post('/delivery-management/create-group', { deliveryDate, groupName, orderUuids }),

  /**
   * Delete an order group
   * DELETE /api/delivery-management/groups/:groupId
   */
  deleteGroup: (groupId) => 
    api.delete(`/delivery-management/groups/${groupId}`),

  /**
   * Assign order group to delivery boy
   * POST /api/delivery-management/assign-group
   */
  assignGroup: (groupUuid, deliveryBoyUuid) => 
    api.post('/delivery-management/assign-group', { groupUuid, deliveryBoyUuid }),

  /**
   * Unassign order group from delivery boy
   * POST /api/delivery-management/unassign-group
   */
  unassignGroup: (groupUuid) => 
    api.post('/delivery-management/unassign-group', { groupUuid }),

  /**
   * Get ungrouped orders for a delivery date
   * GET /api/delivery-management/ungrouped-orders?date=YYYY-MM-DD
   */
  getUngroupedOrders: (deliveryDate) => 
    api.get('/delivery-management/ungrouped-orders', { params: { date: deliveryDate } }),

  /**
   * Get available delivery boys
   * GET /api/delivery-management/available-delivery-boys
   */
  getAvailableDeliveryBoys: () => 
    api.get('/delivery-management/available-delivery-boys'),
};

/**
 * ===================================
 * DELIVERY AGENT API (Delivery Boys)
 * ===================================
 */
export const deliveryAgentAPI = {
  /**
   * Get delivery boy's assigned orders
   * GET /api/delivery-agent/my-orders?date=YYYY-MM-DD&status=xxx
   */
  getMyOrders: (params = {}) => 
    api.get('/delivery-agent/my-orders', { params }),

  /**
   * Get single order details
   * GET /api/delivery-agent/orders/:orderUuid
   */
  getOrderDetails: (orderUuid) => 
    api.get(`/delivery-agent/orders/${orderUuid}`),

  /**
   * Complete delivery with bottle tracking
   * POST /api/delivery-agent/complete-delivery
   */
  completeDelivery: (orderUuid, bottlesGiven = 0, bottlesCollected = 0, bottlesBroken = 0, notes = '') => 
    api.post('/delivery-agent/complete-delivery', {
      orderUuid,
      bottlesGiven,
      bottlesCollected,
      bottlesBroken,
      notes
    }),

  /**
   * Get customer bottle balance
   * GET /api/delivery-agent/customer/:customerUuid/bottle-balance
   */
  getCustomerBottleBalance: (customerUuid) => 
    api.get(`/delivery-agent/customer/${customerUuid}/bottle-balance`),

  /**
   * Get customer bottle history
   * GET /api/delivery-agent/customer/:customerUuid/bottle-history
   */
  getCustomerBottleHistory: (customerUuid, params = {}) => 
    api.get(`/delivery-agent/customer/${customerUuid}/bottle-history`, { params }),

  /**
   * Create standalone bottle transaction (no order)
   * POST /api/delivery-agent/bottle-transaction
   */
  createBottleTransaction: (customerUuid, transactionType, quantity, notes = '') => 
    api.post('/delivery-agent/bottle-transaction', {
      customerUuid,
      transactionType,
      quantity,
      notes
    }),

  /**
   * Get optimized route for delivery boy's orders
   * GET /api/delivery-agent/optimized-route?date=YYYY-MM-DD
   */
  getOptimizedRoute: (deliveryDate) => 
    api.get('/delivery-agent/optimized-route', { params: { date: deliveryDate } }),
};

/**
 * ===================================
 * BOTTLE TRACKING API (Admin)
 * ===================================
 */
export const bottleTrackingAPI = {
  /**
   * Get customer bottle balances with pagination
   * GET /api/bottles/balances?page=1&limit=50&search=xxx
   */
  getCustomerBalances: (params = {}) => 
    api.get('/bottles/balances', { params }),

  /**
   * Get single customer bottle balance details
   * GET /api/bottles/customer/:customerUuid/balance
   */
  getCustomerBalance: (customerUuid) => 
    api.get(`/bottles/customer/${customerUuid}/balance`),

  /**
   * Get bottle transaction history
   * GET /api/bottles/transactions?page=1&limit=50&customerUuid=xxx&startDate=xxx&endDate=xxx
   */
  getTransactions: (params = {}) => 
    api.get('/bottles/transactions', { params }),

  /**
   * Create manual bottle adjustment
   * POST /api/bottles/manual-adjustment
   */
  createManualAdjustment: (customerUuid, adjustmentType, quantity, reason) => 
    api.post('/bottles/manual-adjustment', {
      customerUuid,
      adjustmentType,
      quantity,
      reason
    }),

  /**
   * Get bottle analytics
   * GET /api/bottles/analytics?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   */
  getAnalytics: (startDate, endDate) => 
    api.get('/bottles/analytics', { params: { startDate, endDate } }),

  /**
   * Get filtered transactions
   * GET /api/bottles/transactions-filtered?customerUuid=xxx&transactionType=xxx&startDate=xxx&endDate=xxx
   */
  getFilteredTransactions: (params = {}) => 
    api.get('/bottles/transactions-filtered', { params }),

  /**
   * Export bottle data to CSV
   * GET /api/bottles/export?startDate=xxx&endDate=xxx
   */
  exportToCSV: (startDate, endDate) => 
    api.get('/bottles/export', { 
      params: { startDate, endDate },
      responseType: 'blob'
    }),
};

export default {
  deliveryManagementAPI,
  deliveryAgentAPI,
  bottleTrackingAPI,
};